import { IntlProvider } from 'react-intl';
import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import messages from '../i18n';
import Language from '../i18n/language';
import NavBar from './navbar';
import { CollaboratorRole, CollaboratorStatus, Role, Theme, User } from '../types';
import Quotations from './quotations/quotations';
import MainSettings from './settings/organization/main-settings';
import Projects from './projects/projects';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import useStorage from '../hooks/use-storage';
import { CssBaseline, debounce } from '@mui/material';
import Details from './details/details';
import useLanguage from '../hooks/use-language';
import useAuthorization from '../hooks/use-authorization';
import DetailsSeller from './details/details-seller';
import { useAuth0 } from '@auth0/auth0-react';
import FullScreenLoading from './full-screen-loading';
import { Wrapper } from '@googlemaps/react-wrapper';
import Maintenance from './maintenance';
import client from '../clients/client';
import Collaborators from './collaborators';
import Validation, { ValidationStatus } from './validation';
import BuyTokens from './my-tokens/buy-tokens/buy-tokens';
import QuantitySelectStep from './my-tokens/buy-tokens/quantity-select-step';
import ConfirmationStep from './my-tokens/buy-tokens/confirmation-step';
import ConfirmedStep from './my-tokens/buy-tokens/confirmed-step';
import TokenDetailViewSeller from './my-tokens/detail/DetailStepperSeller';
import TokenDetailViewConstructor from './my-tokens/detail/DetailStepperConstructor';
import TokensSeller from './my-tokens/dashboard/tokens-seller';
import Redeemview from './my-tokens/redeem-view';
import useFirebase from '../hooks/use-firebase';
import MyTokensEmptyState from './my-tokens/my-tokens-empty-state';
import ProjectDetail from './projects/detail/projectDetail';
import Hotjar from '@hotjar/browser';
import RedeemTokensView from './my-tokens/dashboard/RedeemTokensView';
import OneTimeDialog from './ont-time-dialog';
import { useRecoilValue } from 'recoil';
import notificationState from '../atoms/one-time-notification-state';
import disableRootElementScrollState from '../atoms/disable-root-element-scroll-state';
import useOneTimeNotification from '../hooks/use-one-time-notifications';
import useAgreements from '../hooks/use-agreements';
import CostBreakdownComponent from './my-tokens/cost-breakdown/cost-breakdown';
import { NuqleaThemeProvider } from './base-components/nuqlea-theme-provider';
import RedeemViewSeller from './my-tokens/redeem-view/RedeemViewSeller';
import WrapperProposalBackoffice from './details/wrapper-proposal-backoffice';
import { processVisibilityForUserWithAuthRules } from '../helpers/featureFlags';
import { setUsetifulTags } from 'usetiful-sdk';
import { authStatusState } from '../atoms/auth-state';
import AccessError from './auth/access-error';
import AuthError from './auth/auth-error';

const siteId = 2846021;
const hotjarVersion = 6;

const Application = () => {
	const { user: auth0user, isLoading, error, loginWithRedirect, isAuthenticated, logout } = useAuth0();
	const authData = useAuthorization({
		canGetToken: !isLoading,
		redirectOnUnauthorized: '/access-error',
		redirectOnError: '/auth-error',
		autoLogoutOnUnauthorized: false,
	});
	const user = authData.user;
	const authStatus = useRecoilValue(authStatusState);
	const [maintenance, setMaintenance] = useState(false);
	const [language, setLanguage] = useLanguage(() => Language.ES_AR);
	const [theme, setTheme] = useStorage('theme', () => Theme.LIGHT);
	const [remoteConfig] = useFirebase();
	useOneTimeNotification();
	useAgreements();
	const notifications = useRecoilValue(notificationState);
	const disableRootElementScroll = useRecoilValue(disableRootElementScrollState);

	const fetchData = debounce(() => {
		client
			.checkVersion({ fields: { user } })
			.then((data) => {
				if (user && 'version' in user && user?.version !== data?.version) {
					Object.keys(localStorage).forEach((key) => {
						localStorage.removeItem(key);
					});
					localStorage.clear();
					logout({ logoutParams: { returnTo: window.location.origin } });
				} else if (data.maintenance_mode) {
					setTimeout(() => {
						setMaintenance(true);
					}, 350);
				}
			})
			.catch((error) => console.log({ error }));
	}, 250);

	const verifyRoutes = (routes: any[], rules: any[]) => {
		if (!Array.isArray(rules) || rules.length === 0) return routes;

		return routes.map((route) => {
			const rule = rules.find((r) => r.paths?.includes(route.path));
			if (rule) {
				return {
					...route,
					element: (
						<div className='flex flex-col gap-2 items-center justify-center h-[600px] px-4 max-w-lg mx-auto'>
							<div className='text-center text-3xl font-semibold'>
								{rule?.message?.title || 'Sección en mantenimiento'}
							</div>
							<div className='text-center mt-2'>
								{rule?.message?.description ||
									'Detectamos inestabilidad en servicios de terceros y por eso esta sección está temporalmente suspendida. Volvera a estar disponible en cuanto se reestablescan los servicios.'}
							</div>
							{rule?.message?.action && (
								<a
									href={rule?.message?.action?.link}
									target={rule?.message?.action?.target || '_self'}
									className='mt-8 text-white px-6 py-2 bg-black rounded-lg no-underline shadow-md'
								>
									{rule?.message?.action?.label || 'Volver'}
								</a>
							)}
						</div>
					),
				};
			}
			return route;
		});
	};

	useEffect(() => {
		const hostname = window.location.hostname;
		if (hostname !== 'localhost') {
			setMaintenance(remoteConfig?.MANTENIMIENTO);
		}
	}, [remoteConfig]);

	useEffect(() => {
		if (user && Object.keys(user).length !== 0) {
			fetchData();
			if ('email' in user) {
				if (!user.email.split('@')[1].startsWith('nuqlea.com')) {
					Hotjar.init(siteId, hotjarVersion);
					Hotjar.identify(String(user.id), {
						name: user.name,
						email: user.email,
						company: user.name_external,
						id_external: user.id_external,
						permissions: user.admin ? 'admin' : 'regular',
						type_external: user.type_external,
					});
				}

				// Add ustiful service
				setUsetifulTags({
					userId: String(user.id),
					firstName: user.name,
					lastName: user.name,
				});
			}

			(window as any)?.$crisp?.push(['config', 'container:index', [2]]);
			if ('email' in user) {
				(window as any)?.$crisp?.push(['set', 'user:nickname', [user.name]]);
				(window as any)?.$crisp?.push(['set', 'user:email', [user.email]]);
				if (user?.name_external) {
					(window as any)?.$crisp?.push([
						'set',
						'user:company',
						[
							user.name_external,
							{
								id_external: user.id_external,
								name_external: user.name_external,
								type_external: user.type_external,
							},
						],
					]);
				}
			}
		}
	}, [user]);

	useEffect(() => {
		if (disableRootElementScroll) {
			document.documentElement.style.overflow = 'hidden';
		} else {
			document.documentElement.style.overflow = 'auto';
		}
	}, [disableRootElementScroll]);

	const type = user && 'type_external' in user && user?.type_external;

	let routes: any = [];
	let rawRoutes: any = [];

	if (user && type === Role.CONSTRUCTOR) {
		rawRoutes = [
			{
				path: '/',
				element: <Navigate replace to='/profile' />,
			},
			{
				path: '/profile',
				element: <MainSettings />,
			},
		];

		if ('email' in user && processVisibilityForUserWithAuthRules(user as User, remoteConfig?.QUOTES)) {
			rawRoutes.push(
				{
					path: '/quotations',
					element: <Quotations />,
				},
				{
					path: '/list/:listId',
					element: <Details />,
				},
				{
					path: '/list/:listId/proposal',
					element: <WrapperProposalBackoffice />,
				},
				{
					path: '/list/:listId/:orderId',
					element: <Details />,
				},
				{
					path: '/list/:listId/:orderId/proposal',
					element: <WrapperProposalBackoffice />,
				},
			);
		}

		if ('email' in user && processVisibilityForUserWithAuthRules(user as User, remoteConfig?.PROYECTOS)) {
			rawRoutes.push(
				{
					path: '/projects',
					element: <Projects />,
				},
				{
					path: '/projects/new',
					element: <ProjectDetail />,
				},
				{
					path: '/projects/edit/:id',
					element: <ProjectDetail />,
				}
			);
		}

		if ('email' in user && processVisibilityForUserWithAuthRules(user as User, remoteConfig?.ACOPIO)) {
			rawRoutes.push(
				{
					path: '/my-tokens',
					element: <MyTokensEmptyState checkPageVisualization />,
				},
				{
					path: '/empty-state-my-tokens',
					element: <MyTokensEmptyState checkPageVisualization={false} />,
				},
				{
					path: '/my-tokens/:id',
					element: <TokenDetailViewConstructor />,
				},
				{
					path: '/my-tokens/buy',
					element: <BuyTokens />,
					children: [
						{
							path: '/my-tokens/buy/quantity-select',
							element: <QuantitySelectStep />,
						},
						{
							path: '/my-tokens/buy/confirmation-step',
							element: <ConfirmationStep />,
						},
						{
							path: '/my-tokens/buy/confirmed-step',
							element: <ConfirmedStep />,
						},
					],
				},
				{
					path: '/my-tokens/new-redeem',
					element: <RedeemTokensView />,
				},
				{
					path: '/my-tokens/redeem-request/:id',
					element: <Redeemview />,
				}
			);
		}
	} else if (user && type === Role.SELLER) {
		if ('email' in user && processVisibilityForUserWithAuthRules(user as User, remoteConfig?.QUOTES)) {
			rawRoutes.push(
				{
					path: '/quotations',
					element: <Quotations />,
				},
				{
					path: '/list/:listId',
					element: <TokenDetailViewSeller />,
				},
				{
					path: '/list/:listId/:quoteId',
					element: <DetailsSeller />,
				}
			);
		}

		if ('email' in user && processVisibilityForUserWithAuthRules(user as User, remoteConfig?.ACOPIO)) {
			rawRoutes.push(
				{
					path: '/my-tokens',
					element: <TokensSeller />,
				},
				{
					path: '/my-tokens/:id',
					element: <TokenDetailViewSeller />,
				},
				{
					path: '/my-tokens/redeem/:id',
					element: <RedeemTokensView />,
				},
				{
					path: '/my-tokens/redeem-request/:id',
					element: <RedeemViewSeller />,
				},
				{
					path: '/my-tokens/:id/cost-breakdown',
					element: <CostBreakdownComponent />,
				}
			);
		}
	}

	if (user && 'role' in user && user.role !== CollaboratorRole.USER) {
		rawRoutes.push({
			path: '/collaborators',
			element: <Collaborators />,
		});
	}

	routes = verifyRoutes(rawRoutes, remoteConfig?.FEATURE_MANTEINANCE);

	const goToLogin = () => {
		// Force clear browser storage before login
		Object.keys(localStorage).map((key) => {
			localStorage.removeItem(key);
		});
		localStorage.clear();

		loginWithRedirect();
		return <FullScreenLoading />;
	};

	const isUnprotectedRoute = (pathname: string) => {
		return ['/register'].includes(pathname);
	};

	const getRouteUnprotectedPage = (pathname: string) => {
		let routeContent = null;

		return routeContent ? (
			<NuqleaThemeProvider theme={theme}>
				<IntlProvider locale={language} messages={messages[language]}>
					<CssBaseline enableColorScheme>
						<BrowserRouter>
							<Routes>{routeContent}</Routes>
						</BrowserRouter>
					</CssBaseline>
				</IntlProvider>
			</NuqleaThemeProvider>
		) : (
			<></>
		);
	};

	return (
		<NuqleaThemeProvider theme={theme}>
			<IntlProvider locale={language} messages={messages[language]}>
				<Wrapper apiKey='AIzaSyB3YOWVgUXRyCpTbcfvmuPz4LSOqgt6Sps' libraries={['places']} language={language}>
					<CssBaseline enableColorScheme>
						{isAuthenticated && !maintenance ? (
							<BrowserRouter>
								{(authStatus === 'idle' || authStatus === 'loading') && <FullScreenLoading />}
								{authStatus === 'unauthorized' && <AccessError />}
								{authStatus === 'error' && <AuthError />}
								{authStatus === 'ready' && (
									<Box sx={{ display: 'flex' }}>
										{user && 'status' in user ? (
											<>
												{notifications.length > 0 && <OneTimeDialog />}
												{auth0user?.email_verified && CollaboratorStatus.CONFIRMED === user.status && (
													<NavBar remoteConfig={remoteConfig} />
												)}
												<Routes>
													{auth0user?.email_verified &&
														CollaboratorStatus.CONFIRMED === user.status &&
														routes.map((route: any) => {
															const { path, element, children } = route;
															if (!children) {
																return <Route key={path} {...route} />;
															} else {
																return (
																	<Route key={path} path={path} element={element}>
																		{children.map((child: any) => (
																			<Route key={child.path} path={child.path} element={child.element} />
																		))}
																	</Route>
																);
															}
														})}
													{auth0user?.email_verified && CollaboratorStatus.DELETED === user.status && (
														<>
															<Route path={'/'} element={<Navigate replace to='/deleted-user' />} />
															<Route
																key={'/deleted-user'}
																path={'/deleted-user'}
																element={<Validation status={ValidationStatus.DELETED} />}
															/>
														</>
													)}
													{auth0user?.email_verified && CollaboratorStatus.PENDING === user.status && (
														<>
															<Route path={'/'} element={<Navigate replace to='/pending-user-approval' />} />
															<Route
																key={'/pending-user-approval'}
																path={'/pending-user-approval'}
																element={<Validation status={ValidationStatus.PENDING_APPROVAL} />}
															/>
														</>
													)}
													{!auth0user?.email_verified && (
														<>
															<Route path={'/'} element={<Navigate replace to='/pending-email-validation' />} />
															<Route
																key={'/pending-email-validation'}
																path={'/pending-email-validation'}
																element={
																	<Validation status={ValidationStatus.PENDING_VALIDATION} userId={auth0user?.sub} />
																}
															/>
														</>
													)}
												</Routes>
											</>
										) : (
											<FullScreenLoading />
										)}
									</Box>
								)}
							</BrowserRouter>
						) : isUnprotectedRoute(window.location.pathname) ? (
							getRouteUnprotectedPage(window.location.pathname)
						) : (
							<>
								{isLoading && <FullScreenLoading />}
								{maintenance && <Maintenance />}
								{!isLoading && !maintenance && goToLogin()}
							</>
						)}
					</CssBaseline>
				</Wrapper>
			</IntlProvider>
		</NuqleaThemeProvider>
	);
};

export default Application;
